import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { PRIVILIGES_TYPE } from '../../../commonConstant';
import { showError } from '../../Swal';
import Select from 'react-select';
import Loader from '../../../Loader';
import { HandleAPIError } from '../../../commonFunction';
import { login, vmLogin } from '../../../slices/auth-slice';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { ILogin, TPrivilegeType } from '../../../types';

const LoginForm = () => {
  const md5 = require('md5');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [displayPassword, setDisplayPassword] = useState(false);
  const loading = useAppSelector((state) => state.auth.loading);
  const userData = useAppSelector((state) => state.auth);

  const validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    password: Yup.string().required('Password is required'),
    previligesType: Yup.object().shape({
      value: Yup.string().required('Privilege Type is required'),
      label: Yup.string().required('Privilege Type is required'),
    }).required('Privilege Type is required'),
  });

  const handleSubmit = async (values: any) => {
    const { username, password, previligesType } = values;

    const userName = username.trim();
    const passWord = password.trim();
    const privilege = previligesType.value;


    try {
      const vmRequest: ILogin = {
        username: userName,
        password: md5(passWord),
      };

      const vmResponse = await dispatch(vmLogin(vmRequest)).unwrap();

      if (vmResponse?.status !== 0) {
        showError('ERROR', vmResponse?.statusMessage || "")
      } else {
        const request: ILogin = {
          username: userName,
          password: md5(passWord),
          privilege: privilege,
          sessionid: vmResponse?.data?.sessionid
        };

        const responseData = await dispatch(login(request)).unwrap();

        if (responseData?.status !== 0) {
          showError('ERROR', responseData?.statusMessage || "Login unsuccessful")
        }
      }


    } catch (error) {
      HandleAPIError(error);
    }
  };

  useEffect(() => {
    if (userData.publisher === true) {
      navigate('/publisher-dashboard', { replace: true });
    } else if (userData.editor === true) {
      navigate('/editor-dashboard', { replace: true });
    } else if (userData.user === true) {
      navigate('/user-dashboard', { replace: true });
    };
  }, [userData, navigate]);

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      previligesType: null as TPrivilegeType | null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const togglePassword = () => {
    setDisplayPassword(!displayPassword);
  };

  return (
    <>
      <Loader loading={loading} />
      <section className='login-bg-color'>
        <Container className="my-4" >
          <Row className="d-flex justify-content-center min-vh-100 align-items-center">
            <Col lg={4}>
              <Card className="bg-logincart card bg-opacity-10 rounded-3 p-2 my-3 shadow-lg">
                <Card.Body className='bg-logincart p-4 rounded-3'>
                  <h3 className="d-flex justify-content-center">LOGIN</h3>
                  <Card.Text className="d-flex justify-content-center">
                    Please login with your information below
                  </Card.Text>
                  <hr className="horizontalBar" />
                  <Row className="d-flex justify-content-start">
                    <Col md={12}>
                      <h6 className="text-red mb-2">Fill all the fields marked with *</h6>
                    </Col>
                  </Row>
                  {((formik.touched.username && formik.errors.username) ||
                    (formik.touched.password && formik.errors.password) ||
                    (formik.touched.previligesType && formik.errors.previligesType)) && (
                      <div
                        className="alert alert-danger py-2 px-3 mb-1"
                        style={{ backgroundColor: "#ffebee", border: "none", borderRadius: "4px" }}
                      >
                        <h5 className="mb-1" style={{ color: "#d32f2f" }}>
                          Please resolve the following error to continue.
                        </h5>
                        {formik.touched.username && formik.errors.username && (
                          <p className="m-1" style={{ color: "#d32f2f" }}>
                            {formik.errors.username}
                          </p>
                        )}
                        {formik.touched.password && formik.errors.password && (
                          <p className="m-1" style={{ color: "#d32f2f" }}>
                            {formik.errors.password}
                          </p>
                        )}
                        {formik.touched.previligesType && formik.errors.previligesType && (
                          <p className="m-1" style={{ color: "#d32f2f" }}>
                            {typeof formik.errors.previligesType === "string"
                              ? formik.errors.previligesType
                              : "Privilege Type is required"}
                          </p>
                        )}
                      </div>
                    )}
                  <Form onSubmit={formik.handleSubmit}>
                    <Row>
                      <Col md={12} className="mb-3 mt-3">
                        <Form.Label htmlFor="validationCustomUsername" className="">
                          Username
                          <span className="text-red">*</span>
                        </Form.Label>
                        <InputGroup className={formik.touched.username && formik.errors.username ? 'error d-flex' : 'd-flex'}>
                          <Form.Control
                            type="text"
                            id="validationCustomUsername"
                            placeholder="Username"
                            aria-describedby="inputGroupPrepend"
                            name="username"
                            value={formik.values.username}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={12} className="mb-3">
                        <Form.Label htmlFor="validationCustomPassword" className="mb-1">
                          Password<span className="text-red">*</span>
                        </Form.Label>
                        <InputGroup className={formik.touched.password && formik.errors.password ? 'error d-flex' : ''}>
                          <Form.Control
                            type={displayPassword ? "text" : "password"}
                            placeholder="Password"
                            name="password"
                            id="validationCustomPassword"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            style={{
                              borderRadius: "6px",
                              // boxShadow: "none",
                              // outline: "none",
                            }}
                          />
                          <FontAwesomeIcon
                            icon={displayPassword ? faEye : faEyeSlash}
                            size="sm"
                            className="icon-color"
                            onClick={togglePassword}
                            style={{
                              position: 'absolute',
                              top: 11,
                              right: 10,
                              cursor: 'pointer',
                              zIndex: '5',
                            }}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={12} className="mb-3">
                        <Form.Label htmlFor="privilegesType">
                          Privilege
                          <span className="text-red">*</span>
                        </Form.Label>
                        <Select
                          options={PRIVILIGES_TYPE}
                          value={formik.values.previligesType}
                          id="privilegesType"
                          onBlur={formik.handleBlur}
                          name="previligesType"
                          onChange={(d) => formik.setFieldValue('previligesType', d)}
                          className={formik.touched.previligesType && formik.errors.previligesType ? 'error' : ''}
                        />
                      </Col>
                    </Row>





                    <Row>
                      <Col md={12} className="text-center">
                        <Button
                          type="submit"
                          className="btn-success login-btn shadow-sm"
                        >
                          LOGIN
                        </Button>
                      </Col>
                    </Row>

                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default LoginForm;